import { type ReactElement, type ReactNode, useEffect, useState, useMemo } from 'react'
import useOnboard, { type ConnectedWallet, getConnectedWallet } from '@/hooks/wallets/useOnboard'
import useAsync from '@/hooks/useAsync'
import { getSafeInfo } from '@safe-global/safe-gateway-typescript-sdk'
import { useWeb3ReadOnly } from '@/hooks/wallets/web3'
import { useCurrentChain } from '@/hooks/useChains'
import { useRouter } from 'next/router'
import { getNestedWallet } from '@/utils/nested-safe-wallet'
import { sameAddress } from '@safe-global/utils/utils/addresses'
import { WalletContext } from './context'
import { useAddressResolver } from '@/hooks/useAddressResolver'
import { showNotification } from '@/store/notificationsSlice'
import { useAppDispatch } from '@/store'
import { getBlockExplorerLink } from '@/utils/chains'
import { shortenAddress } from '@safe-global/utils/utils/formatters'

const EvmWalletProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const onboard = useOnboard()
  const currentChain = useCurrentChain()
  const web3ReadOnly = useWeb3ReadOnly()
  const router = useRouter()
  const onboardWallets = onboard?.state.get().wallets || []
  const [wallet, setWallet] = useState<ConnectedWallet | null>(getConnectedWallet(onboardWallets))
  const { ens, resolving: isResolvingAddress } = useAddressResolver(wallet?.address ?? '', true)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!currentChain || !wallet?.address || isResolvingAddress || !!ens) return

    const timeout = setTimeout(() => {
      dispatch(
        showNotification({
          title: `Your wallet account is not active on ${currentChain.chainName}`,
          message: `Activate it by transferring any amount of ℏ or tokens to ${shortenAddress(wallet.address)}`,
          detailedMessage: `This EVM address is not associated with an account on ${currentChain.chainName}, please transfer some funds to it to activate it or change the chain`,
          groupKey: 'wallet-not-active',
          variant: 'warning',
          link: getBlockExplorerLink(currentChain, wallet.address),
        }),
      )
    }, 2000)

    return () => clearTimeout(timeout)
  }, [wallet?.address, ens, isResolvingAddress, currentChain, dispatch])

  const [signerAddress, setSignerAddress] = useState<string>()

  const [nestedSafeInfo] = useAsync(() => {
    if (signerAddress && !sameAddress(signerAddress, wallet?.address) && currentChain) {
      return getSafeInfo(currentChain.chainId, signerAddress)
    }
  }, [currentChain, signerAddress, wallet?.address])

  useEffect(() => {
    if (!onboard) return

    const walletSubscription = onboard.state.select('wallets').subscribe((wallets) => {
      const newWallet = getConnectedWallet(wallets)

      setWallet(newWallet)
    })

    return () => {
      walletSubscription.unsubscribe()
    }
  }, [onboard])

  const signer = useMemo(() => {
    if (wallet && nestedSafeInfo && web3ReadOnly) {
      return getNestedWallet(wallet, nestedSafeInfo, web3ReadOnly, router)
    }
    return wallet
  }, [wallet, nestedSafeInfo, web3ReadOnly, router])

  return (
    <WalletContext.Provider
      value={{
        connectedWallet: wallet,
        signer,
        setSignerAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export default EvmWalletProvider
